import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { InvoiceSetupService } from '../services/invoice-setup.service';

/**
 * Route guard enforcing mandatory onboarding completion.
 * Redirects users to /onboarding if setup is incomplete.
 * Prevents completed users from re-entering /onboarding.
 *
 * ⚠️ Must be wired as `canActivateChild` on the shell route — not `canActivate`.
 * Angular only runs a parent's `canActivate` when that parent is *activated*, so
 * as `canActivate` it fired once on entry and then never again while the user
 * clicked between sibling children: the wizard was trivially escapable.
 * `canActivateChild` re-runs for every descendant activation. Routes that live
 * outside the shell (`/units`, `/docs/:slug`) must list it in `canActivate`
 * themselves.
 */
export const onboardingGuard: CanActivateFn = (route, state) => {
  const invoiceSetupService = inject(InvoiceSetupService);
  const router = inject(Router);

  return invoiceSetupService.resolveOnboardingCompleted().pipe(
    map((onboardingCompleted) => {
      const isOnboardingUrl = state.url.startsWith('/onboarding');

      if (!onboardingCompleted) {
        return isOnboardingUrl ? true : router.createUrlTree(['/onboarding']);
      }

      return isOnboardingUrl ? router.createUrlTree(['/']) : true;
    }),
    // Fail open so a status outage cannot lock everyone out of the app. The
    // backend gate still returns 428 on every non-exempt endpoint, so an
    // unonboarded tenant lands on a broken shell rather than slipping through.
    // Log it — silent failure here is what made this guard look like a no-op.
    catchError((error) => {
      console.error('Onboarding status check failed:', error);
      return of(true);
    })
  );
};
